import { createClient } from '@supabase/supabase-js';
import { optimizeImageForUpload, validateImageFile } from './imageOptimizationService.js';

// Supabase: endast miljövariabler - committa aldrig riktiga nycklar eller .env.
// Använd enbart anon/public-nyckeln här; service_role får aldrig ligga i frontend.
// (Vid build ersätter CRA REACT_APP_* med värden från miljön - skydda data med RLS.)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);
// In-memory cache to avoid repeated round-trips while the app is open.
let servicesCache = null;
let servicesInFlightPromise = null;
const SERVICES_CACHE_KEY = 'rb_services_cache_v2';
const SERVICES_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const FALLBACK_SERVICE_IMAGE = '/Images/works.jpg';
const DEFAULT_SERVICES = [
  {
    id: 'fallback-redovisning',
    title: 'Bokföring och redovisning',
    content: 'Vi hanterar den löpande redovisningen så att du kan fokusera på din verksamhet.',
    buttonText: 'Läs mer',
    buttonCTA: 'Läs mer',
    image: FALLBACK_SERVICE_IMAGE,
    includedIntro: '',
    includedItems: [],
    fitsIntro: '',
    fitsItems: [],
  },
  {
    id: 'fallback-deklaration',
    title: 'Deklaration och skatt',
    content: 'Vi hjälper dig med deklaration och skattefrågor på ett tydligt och tryggt sätt.',
    buttonText: 'Läs mer',
    buttonCTA: 'Läs mer',
    image: FALLBACK_SERVICE_IMAGE,
    includedIntro: '',
    includedItems: [],
    fitsIntro: '',
    fitsItems: [],
  },
];

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readServicesStorageCache = () => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(SERVICES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { items: parsed, isFresh: true };
    }
    const items = Array.isArray(parsed?.items) ? parsed.items : null;
    const savedAt = Number(parsed?.savedAt || 0);
    if (!items) return null;
    const isFresh = savedAt > 0 ? Date.now() - savedAt < SERVICES_CACHE_MAX_AGE_MS : true;
    return { items, isFresh };
  } catch (_) {
    return null;
  }
};

const writeServicesStorageCache = (items) => {
  if (!canUseStorage() || !Array.isArray(items)) return;
  try {
    window.localStorage.setItem(SERVICES_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      items,
    }));
  } catch (_) {
  }
};

const getServicesFallback = () => {
  const storageCache = readServicesStorageCache();
  if (Array.isArray(storageCache?.items) && storageCache.items.length > 0) {
    return storageCache.items;
  }
  return DEFAULT_SERVICES;
};

const parseStringArray = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  return [];
};

const mapDbToModel = (row) => ({
  id: row.id,
  title: row.title,
  content: row.content,
  buttonText: row.buttontext ?? row.buttonText ?? row.buttoncta ?? row.buttonCTA ?? '',
  buttonCTA: row.buttoncta ?? row.buttonCTA ?? row.buttontext ?? row.buttonText ?? '',
  image: row.image,
  includedIntro: row.included_intro ?? row.includedIntro ?? '',
  includedItems: parseStringArray(row.included_items ?? row.includedItems),
  fitsIntro: row.fits_intro ?? row.fitsIntro ?? '',
  fitsItems: parseStringArray(row.fits_items ?? row.fitsItems),
});

const mapModelToDb = (data) => ({
  title: data.title ?? null,
  content: data.content ?? null,
  buttontext: data.buttonText ?? data.buttontext ?? data.buttonCTA ?? data.buttoncta ?? '',
  buttoncta: data.buttonCTA ?? data.buttoncta ?? data.buttonText ?? data.buttontext ?? '',
  image: data.image ?? null,
  included_intro: data.includedIntro ?? data.included_intro ?? '',
  included_items: Array.isArray(data.includedItems)
    ? data.includedItems.map(String).filter(Boolean)
    : parseStringArray(data.included_items),
  fits_intro: data.fitsIntro ?? data.fits_intro ?? '',
  fits_items: Array.isArray(data.fitsItems)
    ? data.fitsItems.map(String).filter(Boolean)
    : parseStringArray(data.fits_items),
});

const fetchAllServices = async () => {
  // Single select to load all services for public rendering and admin lists.
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('id', { ascending: true });
  if (error) throw error;
  const mapped = (data || []).map(mapDbToModel);
  writeServicesStorageCache(mapped);
  return mapped;
};

const uploadServiceImage = async (file) => {
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.reason);
  }

  // Normalize and compress before storing in Supabase Storage.
  const optimized = await optimizeImageForUpload(file);
  const fileName = `${Date.now()}.${optimized.extension}`;
  const filePath = `services/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('images')
    .upload(filePath, optimized.file, {
      contentType: optimized.mimeType,
      cacheControl: '31536000',
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data: publicUrl } = supabase.storage
    .from('images')
    .getPublicUrl(filePath);

  return {
    publicUrl: publicUrl.publicUrl,
    format: optimized.extension.toUpperCase(),
    optimized: optimized.optimized,
  };
};

// Abstraktion för datakommunikation
export const serviceService = {
  // Reuse last fetch to keep UI instant when revisiting the list.
  getCached: () => servicesCache,

  getAll: async ({ force = false } = {}) => {
    if (!force && Array.isArray(servicesCache)) {
      return servicesCache;
    }

    if (!force) {
      const storageCache = readServicesStorageCache();
      if (Array.isArray(storageCache?.items) && storageCache.items.length > 0 && storageCache.isFresh) {
        servicesCache = storageCache.items;
        return storageCache.items;
      }
    }

    if (!force && servicesInFlightPromise) {
      return servicesInFlightPromise;
    }

    servicesInFlightPromise = fetchAllServices()
      .then((items) => {
        servicesCache = items;
        return items;
      })
      .catch((error) => {
        const fallbackItems = getServicesFallback();
        servicesCache = fallbackItems;
        if (fallbackItems.length > 0) {
          return fallbackItems;
        }
        throw error;
      })
      .finally(() => {
        servicesInFlightPromise = null;
      });

    return servicesInFlightPromise;
  },

  warmup: async () => {
    try {
      await serviceService.getAll();
    } catch (_) {
    }
  },

  getById: async (id) => {
    try {
      const { data, error } = await supabase.from('services').select('*').eq('id', id).single();
      if (error) throw error;
      return mapDbToModel(data);
    } catch (_) {
      const source = Array.isArray(servicesCache) && servicesCache.length > 0
        ? servicesCache
        : getServicesFallback();
      return source.find((service) => String(service.id) === String(id)) ?? null;
    }
  },

  create: async (data) => {
    const payload = mapModelToDb(data);
    const { data: result, error } = await supabase.from('services').insert(payload).select().single();
    if (error) throw error;
    const created = mapDbToModel(result);
    if (Array.isArray(servicesCache)) {
      servicesCache = [...servicesCache, created];
      writeServicesStorageCache(servicesCache);
    }
    return created;
  },

  update: async (id, data) => {
    const payload = mapModelToDb(data);
    const { data: result, error } = await supabase.from('services').update(payload).eq('id', id).select().single();
    if (error) throw error;
    const updated = mapDbToModel(result);
    if (Array.isArray(servicesCache)) {
      servicesCache = servicesCache.map((service) => (service.id === id ? updated : service));
      writeServicesStorageCache(servicesCache);
    }
    return updated;
  },

  delete: async (id) => {
    const { error } = await supabase.from('services').delete().eq('id', id);
    if (error) throw error;
    if (Array.isArray(servicesCache)) {
      servicesCache = servicesCache.filter((service) => service.id !== id);
      writeServicesStorageCache(servicesCache);
    }
  },

  uploadImage: uploadServiceImage,
};
